import type { ProFormSelectProps } from '@ant-design/pro-components';
import { ProFormSelect } from '@ant-design/pro-components';
import { Button, Spin } from 'antd';
import React from 'react';
import { usePagedSelectOptions } from '../hooks/usePagedSelectOptions';
import {
  batchGetEmployeeDirectory,
  type EmployeeDirectorySummary,
  searchEmployeeDirectory,
} from '../services/employee-directory';
import { formatEmployeeDisplay } from './employee-display-utils';
import PagedSelectPopupFooter from './PagedSelectPopupFooter';

type EmployeeSelectProps = {
  name?: string | string[];
  label?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  employeeStatus?: string;
  employeeStatusList?: string[];
  excludeEmployeeUuid?: string;
  excludeUuids?: string[];
  excludeOpenResignation?: boolean;
  compactDisplay?: boolean;
  extraOptions?: Array<{ label: string; value: string }>;
  snapshot?: {
    uuid?: string;
    name?: string | null;
    employeeNo?: string | null;
    departmentName?: string | null;
  };
  style?: ProFormSelectProps['style'];
  fieldProps?: ProFormSelectProps['fieldProps'];
  colProps?: ProFormSelectProps['colProps'];
};

export const formatEmployeeSelectOptionLabel = (
  employee: Pick<EmployeeDirectorySummary, 'name' | 'employeeNo' | 'mobile'> & {
    departmentName?: string | null;
    departments?: EmployeeDirectorySummary['departments'];
  },
  _compactDisplay = false,
) => formatEmployeeDisplay(employee.name, employee.employeeNo) || '未知员工';

const EmployeeSelect: React.FC<EmployeeSelectProps> = ({
  name = 'employeeUuid',
  label = '员工',
  required = true,
  disabled = false,
  readonly = false,
  employeeStatus,
  employeeStatusList,
  excludeEmployeeUuid,
  excludeUuids = [],
  excludeOpenResignation = false,
  compactDisplay = false,
  extraOptions = [],
  snapshot,
  style,
  fieldProps,
  colProps,
}) => {
  const snapshotOption = React.useMemo(() => {
    if (!snapshot?.uuid) return undefined;
    const identity = formatEmployeeDisplay(snapshot.name, snapshot.employeeNo);
    if (!identity) return undefined;
    return {
      label: identity,
      value: snapshot.uuid,
    };
  }, [snapshot]);
  const selectedUuid = String(snapshot?.uuid || '').trim();
  const [resolvedSelectedOption, setResolvedSelectedOption] = React.useState<{
    label: string;
    value: string;
  }>();

  const excludedRef = React.useMemo(
    () => new Set([excludeEmployeeUuid, ...excludeUuids].filter(Boolean)),
    [excludeEmployeeUuid, excludeUuids],
  );

  const buildOptions = React.useCallback(
    (employees: EmployeeDirectorySummary[]) =>
      employees
        .filter((employee) => !excludedRef.has(employee.employeeUuid))
        .map((employee) => ({
          label: formatEmployeeSelectOptionLabel(employee, compactDisplay),
          value: employee.employeeUuid,
          name: employee.name,
          employeeNo: employee.employeeNo,
        })),
    [compactDisplay, excludedRef],
  );

  const loadEmployeePage = React.useCallback(
    async ({
      keyword: nextKeyword,
      page,
      pageSize,
    }: {
      keyword: string;
      page: number;
      pageSize: number;
    }) => {
      const result = await searchEmployeeDirectory({
        keyword: nextKeyword,
        page,
        pageSize,
        memberStatus: 'active',
        employeeLifecycleStatus: employeeStatus,
        employeeLifecycleStatuses: employeeStatusList,
        excludeOpenResignation: excludeOpenResignation || undefined,
      });
      return {
        ...result,
        list: buildOptions(result.list),
      };
    },
    [buildOptions, employeeStatus, employeeStatusList, excludeOpenResignation],
  );

  const pagedOptions = usePagedSelectOptions({
    cacheKey: JSON.stringify({
      employeeStatus,
      employeeStatusList,
      excludeEmployeeUuid,
      excludeUuids,
      excludeOpenResignation,
    }),
    loadPage: loadEmployeePage,
  });

  const hasSelectedOption = [
    ...extraOptions,
    ...(snapshotOption ? [snapshotOption] : []),
    ...pagedOptions.options,
  ].some((option) => option.value === selectedUuid);

  React.useEffect(() => {
    if (!selectedUuid || hasSelectedOption) {
      setResolvedSelectedOption(undefined);
      return;
    }

    let cancelled = false;
    setResolvedSelectedOption({
      label: '正在加载员工信息',
      value: selectedUuid,
    });
    void batchGetEmployeeDirectory([selectedUuid])
      .then(([employee]) => {
        if (cancelled) return;
        setResolvedSelectedOption({
          label: employee
            ? formatEmployeeSelectOptionLabel(employee, compactDisplay)
            : '员工信息不可用',
          value: selectedUuid,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedSelectedOption({
            label: '员工信息不可用',
            value: selectedUuid,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [compactDisplay, hasSelectedOption, selectedUuid]);

  const mergedOptions = React.useMemo(() => {
    const existing = new Map<string, { label: string; value: string }>();
    [
      ...(resolvedSelectedOption ? [resolvedSelectedOption] : []),
      ...extraOptions,
      ...(snapshotOption ? [snapshotOption] : []),
      ...pagedOptions.options,
    ]
      .filter((item) => !excludedRef.has(item.value))
      .forEach((item) => {
        if (item.value && !existing.has(item.value))
          existing.set(item.value, item);
      });
    return Array.from(existing.values());
  }, [
    excludedRef,
    extraOptions,
    pagedOptions.options,
    resolvedSelectedOption,
    snapshotOption,
  ]);

  return (
    <ProFormSelect
      name={name}
      label={label}
      readonly={readonly}
      style={{ width: '100%', ...style }}
      rules={
        required ? [{ required: true, message: `请选择${label}` }] : undefined
      }
      colProps={colProps}
      showSearch
      placeholder="可按姓名、手机号、工号搜索"
      options={mergedOptions}
      fieldProps={{
        ...fieldProps,
        disabled,
        filterOption: false,
        optionLabelProp: 'label',
        loading: pagedOptions.loadingInitial,
        onOpenChange: (open: boolean) => {
          fieldProps?.onOpenChange?.(open);
          if (open) void pagedOptions.ensureLoaded();
        },
        onSearch: (value: string) => {
          fieldProps?.onSearch?.(value);
          pagedOptions.search(value);
        },
        onPopupScroll: (event: React.UIEvent<HTMLDivElement>) => {
          fieldProps?.onPopupScroll?.(event);
          const target = event.currentTarget;
          if (
            target.scrollTop + target.clientHeight >=
              target.scrollHeight - 16 &&
            pagedOptions.hasMore &&
            !pagedOptions.loading
          ) {
            void pagedOptions.loadMore();
          }
        },
        popupRender: (originNode) => (
          <>
            {fieldProps?.popupRender?.(originNode) ?? originNode}
            <PagedSelectPopupFooter
              loadingMore={pagedOptions.loadingMore}
              loadMoreError={pagedOptions.loadMoreError}
              onRetry={() => void pagedOptions.retry()}
            />
          </>
        ),
        notFoundContent: pagedOptions.loadingInitial ? (
          <Spin size="small" />
        ) : pagedOptions.initialError ? (
          <Button type="link" onClick={pagedOptions.retry}>
            加载失败，重试
          </Button>
        ) : (
          fieldProps?.notFoundContent
        ),
        style: { width: '100%', ...fieldProps?.style },
      }}
    />
  );
};

export default EmployeeSelect;
